import { useEffect, useRef, type RefObject } from 'react';

/**
 * Calls `handler` when a pointerdown lands outside every supplied element.
 * Listens on `pointerdown` (not `click`) so a menu closes before the underlying
 * control receives the press, and ignores presses on any extra `ignore` refs —
 * typically the trigger button, which toggles on its own click.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: PointerEvent) => void,
  options: { enabled?: boolean; ignore?: RefObject<HTMLElement | null>[] } = {},
): void {
  const { enabled = true, ignore } = options;
  const handlerRef = useRef(handler);
  const ignoreRef = useRef(ignore);

  useEffect(() => {
    handlerRef.current = handler;
    ignoreRef.current = ignore;
  }, [handler, ignore]);

  useEffect(() => {
    if (!enabled) return;

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (ref.current?.contains(target)) return;
      for (const other of ignoreRef.current ?? []) {
        if (other.current?.contains(target)) return;
      }
      handlerRef.current(event);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [ref, enabled]);
}
